import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { DateTime } from 'luxon';
import {
    Outlet, useMatch, useNavigate, useParams,
} from 'react-router-dom';
import { ButtonOption } from 'react-rainbow-components';
import Button from 'components/Button';
import ButtonIcon from 'components/ButtonIcon';
import Insights from 'components/icons/insight';
import SearchIcon from 'components/icons/search';
import BoardIcon from 'components/icons/board';
import TableViewIcon from 'components/icons/tableView';
import TasksIcon from 'components/icons/tasks';
import Plus from 'components/icons/plus';
import SortByPicker from 'components/SortByPIcker';
import PipelineSwitcher from 'components/PipelineSwitcher';
import useStoredSort from 'hooks/useStoredSort';
import usePipelines from 'data/firestore/agent/pipeline/useCollection';
import usePipelineStages from 'data/firestore/agent/pipeline/stage/useCollection';
import { Stage } from 'data/firestore/agent/pipeline/stage/types';
import { EntityGet } from 'data/firestore/types';
import useNavigateWithQuery from 'hooks/useNavigateWithQuery';
import useSearchFilters from 'hooks/useSearchFilters';
import useUserRoles from 'hooks/useUserRoles';
import { OPPORTUNITY_EVENTS } from 'analytics/posthog/types';
import useDebouncedTrack from 'hooks/useDebouncedTrack';
import { ViewContextType } from '../types';
import { DEFAULT_SORT_OPPORTUNITIES } from './contants';
import {
    Container,
    StyledCard,
    SearchContainer,
    SearchInput,
    SearchInputSpacer,
    Content,
    StyledBadgeOverlay,
    StyledButtonGroupPicker,
    RightContent,
} from './styled';
import useManageOpportunities from '../../../data/hooks/useManageOpportunities';
import FilterButton from './filterButton';

const opportunitySearchFilters = ['search', 'owner', 'score', 'stage', 'createdAt', 'amount', 'tags'];
const opportunityFilterCountKeys = ['owner', 'score', 'stage', 'createdAt', 'amount', 'tags'];
const todoSearchFilters = ['todoDueAt', 'todoAssignee', 'todoPriority', 'todoStatus'];
const allSearchFilters = [...opportunitySearchFilters, ...todoSearchFilters];
const defaultTodoStatusFilter = 'pending,in_progress';
const defaultTodoDueAtFilter = `${DateTime.now().startOf('day').toISO() || ''},${DateTime.now().endOf('day').toISO() || ''}`;

const sortFields = [
    { name: 'score', label: 'Score' },
    { name: 'createdAt', label: 'Create Date' },
    { name: 'updatedAt', label: 'Last Updated' },
];

const OpportunitiesViewPage = () => {
    const { agentId = '', pipelineId: activePipelineId = '' } = useParams();
    const { isAdmin } = useUserRoles();
    const navigate = useNavigate();
    const navigateWithQuery = useNavigateWithQuery();
    const match = useMatch(':agentId/opportunities/:pipeline/view/:viewType');
    const viewType = (match?.params?.viewType as 'board' | 'list' | 'todos') || 'board';
    const isTodosView = viewType === 'todos';
    const { debounceTrack: trackSearch } = useDebouncedTrack(500);
    const [searchValue, setSearchValue] = useState('');

    const [sort, setSort] = useStoredSort('opportunities', DEFAULT_SORT_OPPORTUNITIES);

    const {
        values: filterValues,
        getValue: getFilterValue,
        setValue: setFilterValue,
    } = useSearchFilters({
        filterNames: allSearchFilters,
    });
    const previousViewTypeRef = useRef<typeof viewType | null>(null);

    useEffect(() => {
        if (searchValue.length > 0) {
            trackSearch(OPPORTUNITY_EVENTS.SEARCHED, { query: searchValue, resultsCount: 0 });
        }
    }, [searchValue, trackSearch]);

    useEffect(
        () => {
            const isInitialRender = previousViewTypeRef.current === null;
            const isEnteringTodosView = previousViewTypeRef.current !== 'todos' && viewType === 'todos';

            if ((isInitialRender || isEnteringTodosView) && viewType === 'todos') {
                const hasTodoStatusFilter = getFilterValue('todoStatus') !== null;
                const hasTodoDueAtFilter = getFilterValue('todoDueAt') !== null;

                if (!hasTodoStatusFilter && !hasTodoDueAtFilter) {
                    setFilterValue('todoStatus', defaultTodoStatusFilter);
                    setFilterValue('todoDueAt', defaultTodoDueAtFilter);
                }
            }

            previousViewTypeRef.current = viewType;
        },
        [viewType, getFilterValue, setFilterValue],
    );

    const activeFiltersCount = useMemo(
        () => (isTodosView ? todoSearchFilters : opportunityFilterCountKeys).reduce(
            (count, key) => (
                getFilterValue(key) !== null
                    ? count + 1
                    : count
            ),
            0,
        ),
        [isTodosView, getFilterValue],
    );

    const { create: createNewOpportunity } = useManageOpportunities({ agentId });

    const {
        data: pipelines = [],
        isLoading: isLoadingPipelines,
    } = usePipelines(
        agentId,
        {
            disabled: !agentId,
            track: [agentId],
        },
    );

    const {
        data: pipelineStages = [],
        isLoading: isLoadingPipelineStages,
    } = usePipelineStages(
        agentId,
        activePipelineId,
        {
            disabled: !agentId || !activePipelineId,
            track: [activePipelineId],
        },
    );

    const orderedPipelineStages = useMemo(
        () => {
            const pipelineStageOrder = pipelines.find(
                // eslint-disable-next-line @typescript-eslint/no-shadow
                (pipeline) => pipeline.id === activePipelineId,
            )?.stages || [];

            return pipelineStageOrder.reduce(
                (activeStages: EntityGet<Stage>[], stageId: string) => {
                    const found = pipelineStages.find((stage) => stage.id === stageId);
                    if (found) {
                        return [...activeStages, found];
                    }

                    return activeStages;
                },
                [],
            );
        },
        [activePipelineId, pipelineStages, pipelines],
    );

    const handleAddNewOpportunity = useCallback(
        () => {
            const initialStage = orderedPipelineStages[0] as { id?: string } | undefined;
            trackSearch(OPPORTUNITY_EVENTS.CREATED, {
                pipelineId: activePipelineId,
                initialStage: initialStage?.id || '',
                source: 'header_button',
            });
            createNewOpportunity({
                pipeline: activePipelineId,
                stageId: orderedPipelineStages[0],
            });
        },
        [createNewOpportunity, activePipelineId, orderedPipelineStages, trackSearch],
    );

    const contextValue = useMemo(
        () => ({
            selectedPipeline: activePipelineId,
            viewType,
            isLoading: isLoadingPipelines || isLoadingPipelineStages,
            pipelines,
            stages: orderedPipelineStages,
            searchQuery: isTodosView ? '' : (getFilterValue('search') || ''),
            filters: filterValues,
            sortBy: sort,
            searchFilters: isTodosView ? todoSearchFilters : opportunitySearchFilters,
            activeFiltersCount,
        }) satisfies ViewContextType,
        [
            activePipelineId,
            viewType,
            isTodosView,
            isLoadingPipelines,
            isLoadingPipelineStages,
            pipelines,
            orderedPipelineStages,
            filterValues,
            activeFiltersCount,
            getFilterValue,
            sort,
        ],
    );

    const handleSwitchPipeline = useCallback((value: string) => {
        if (value === 'manage') {
            navigateWithQuery(`/${agentId}/pipelines`);
            return;
        }
        trackSearch(OPPORTUNITY_EVENTS.PIPELINE_SWITCHED, {
            fromPipeline: activePipelineId,
            toPipeline: value,
        });
        navigateWithQuery(`/${agentId}/opportunities/${value}`);
    }, [navigateWithQuery, agentId, activePipelineId, trackSearch]);

    return (
        <Container>
            <Content>
                <StyledCard>
                    <SearchContainer>
                        <PipelineSwitcher
                            agentId={agentId}
                            value={activePipelineId}
                            onChange={handleSwitchPipeline}
                            hideManageAction={!isAdmin}
                        />
                        <StyledButtonGroupPicker
                            value={viewType}
                            onChange={(nextViewType) => {
                                trackSearch(OPPORTUNITY_EVENTS.VIEW_SWITCHED, { view: nextViewType as 'board' | 'list' | 'todos' });
                                navigateWithQuery(nextViewType as string);
                            }}
                        >
                            <ButtonOption name="board" label={<BoardIcon />} />
                            <ButtonOption name="list" label={<TableViewIcon />} />
                            <ButtonOption name="todos" label={<TasksIcon />} />
                        </StyledButtonGroupPicker>
                        {!isTodosView && (
                            <SearchInput
                                type="search"
                                placeholder="Search opportunity..."
                                variant="bare"
                                icon={<SearchIcon />}
                                borderRadius="semi-rounded"
                                onChange={(e) => {
                                    setSearchValue(e.target.value);
                                    setFilterValue('search', e.target.value || null);
                                }}
                                value={getFilterValue('search') || ''}
                            />
                        )}
                        {isTodosView && <SearchInputSpacer aria-hidden="true" />}
                        <RightContent>
                            {!isTodosView && (
                                <SortByPicker
                                    sortFields={sortFields}
                                    value={sort}
                                    onChange={(newSort) => {
                                        trackSearch(OPPORTUNITY_EVENTS.SORTED_BY, {
                                            sortBy: newSort.sortBy as 'score' | 'createdAt' | 'updatedAt',
                                            direction: newSort.order as 'asc' | 'desc',
                                        });
                                        setSort(newSort);
                                    }}
                                    prefixLabel="Sort by:"
                                    iconPosition="right"
                                    borderRadius="semi-rounded"
                                />
                            )}
                            <ButtonIcon
                                icon={<Insights />}
                                onClick={() => {
                                    trackSearch(
                                        OPPORTUNITY_EVENTS.INSIGHTS_VIEWED,
                                        { pipelineId: activePipelineId },
                                    );
                                    navigate(`/${agentId}/insights/opportunities?pipeline=${activePipelineId}`);
                                }}
                                borderRadius="semi-rounded"
                                tooltip="View Insights"
                            />
                            {!isTodosView && (
                                <StyledBadgeOverlay
                                    value={activeFiltersCount}
                                    isHidden={activeFiltersCount === 0}
                                    variant="brand"
                                    position="top-left"
                                >
                                    <FilterButton
                                        label="Filters"
                                        agentId={agentId}
                                        pipelineId={activePipelineId}
                                        viewType={viewType}
                                    />
                                </StyledBadgeOverlay>
                            )}
                            <Button
                                variant="brand"
                                size="medium"
                                borderRadius="semi-rounded"
                                onClick={handleAddNewOpportunity}
                            >
                                <Plus className="rainbow-m-right_small" />
                                New
                            </Button>
                        </RightContent>
                    </SearchContainer>
                    <Outlet context={contextValue} />
                </StyledCard>
            </Content>
        </Container>
    );
};

export default OpportunitiesViewPage;
